import { Json } from '@/integrations/supabase/types';
import { ProfileData } from '@/hooks/useProfiles';
import { normalizePortfolioItems } from '@/hooks/portfolio/usePortfolioNormalizer';

/**
 * Transforms raw profile data from the database into a structured ProfileData object
 */
export const transformProfileData = (profile: any): ProfileData => {
  try {
    // Extract raw portfolio items from database response
    const rawPortfolioItems = profile.portfolio_items as Json[] || [];
    
    // Normalize portfolio items with error handling
    let normalizedPortfolioItems = [];
    try {
      normalizedPortfolioItems = (rawPortfolioItems as Json[])
        .filter(item => (item as any)?.status !== "draft")
        .map(item => ({
          id: (item as any)?.id || undefined,
          title: (item as any)?.title || '',
          description: (item as any)?.description || '',
          image_url: (item as any)?.image_url || '',
          images: (item as any)?.images || [],
          created_at: (item as any)?.created_at || new Date().toISOString(),
          is_featured: (item as any)?.is_featured || false,
          status: (item as any)?.status || 'published'
        }));
    } catch (err) {
      console.error(`Error normalizing portfolio items for user ${profile.id}:`, err);
      normalizedPortfolioItems = []; // Fallback to empty array
    }
    
    // Create a typed object with all required fields
    return {
      id: profile.id,
      username: profile.username,
      display_name: profile.display_name || profile.username, // Add display_name with fallback
      email: profile.email,
      avatar_url: profile.avatar_url,
      bio: profile.bio,
      profession: profile.profession,
      location: profile.location,
      years_experience: profile.years_experience,
      education: profile.education,
      portfolio_url: profile.portfolio_url,
      // Handle potentially missing fields with null fallbacks
      website_url: null, // Default to null as this may not exist in database response
      linkedin_username: profile.linkedin_username,
      twitter_username: null, // Default to null as this may not exist in database response 
      instagram_username: profile.instagram_username,
      skills: profile.skills,
      languages: profile.languages,
      created_at: profile.created_at,
      updated_at: profile.updated_at,
      is_admin: profile.is_admin,
      like_count: profile.like_count,
      view_count: profile.view_count,
      is_liked: false,
      portfolio_items: normalizedPortfolioItems,
      experience_items: (profile.experience_items as Json[] || []).map(item => ({
        id: (item as any)?.id || undefined,
        position: (item as any)?.position || '',
        company: (item as any)?.company || '',
        start_date: (item as any)?.start_date || '',
        end_date: (item as any)?.end_date,
        current: (item as any)?.current || false,
        description: (item as any)?.description,
        location: (item as any)?.location
      })),
      username_slug: profile.username_slug
    };
  } catch (err) {
    console.error(`Error processing profile ${profile.id}:`, err);
    // Return minimal valid profile to prevent breaking the entire list
    return {
      id: profile.id,
      username: profile.username,
      display_name: profile.display_name || profile.username, // Add display_name with fallback
      email: profile.email,
      avatar_url: profile.avatar_url,
      bio: profile.bio || null,
      profession: profile.profession || null,
      location: profile.location || null,
      years_experience: profile.years_experience || null,
      education: profile.education || null,
      portfolio_url: profile.portfolio_url || null,
      website_url: null,
      linkedin_username: profile.linkedin_username || null,
      twitter_username: null,
      instagram_username: profile.instagram_username || null,
      skills: profile.skills || [],
      languages: profile.languages || [],
      created_at: profile.created_at,
      updated_at: profile.updated_at,
      is_admin: profile.is_admin || false,
      like_count: profile.like_count || 0,
      view_count: profile.view_count || 0,
      is_liked: false,
      portfolio_items: [],
      experience_items: [],
      username_slug: profile.username_slug
    };
  }
};
